/**
 * Players (spec 02 §3) - owned by the PLAYER stream, ported from the rebb64 disassembly of the C64
 * game (D_1CBD's IRQ loop, D_2162 and what it calls, spawn_animation / spawn_timer, and the main loop's
 * check_player_state, check_join_game and D_0AAB). The world calls `updatePlayers` every I-tick and
 * `placeAtStart` at level start; the other streams call `killPlayer`, `bounce`, `pushBubbles` (once per
 * main-loop pass) and `blowOrigin` (inside `blowBubble`); the flow calls `isSettled`; the renderer reads
 * `playerPose`.
 */

import { heartSpeed } from '../items';
import { runPlayer, restartJump } from './handler';
import { mainLoop } from './life';
import { joyBits } from './state';
import type { Command, Facing, PlayerId, World } from '../types';

export { blowOrigin } from './blow';
export { killPlayer, placeAtStart } from './life';
export { playerPose, type PlayerPose, type PlayerPoseName } from './pose';
export { pushBubbles } from './push';

/**
 * One I-tick of both players (P2 first, as the C64's IRQ loop runs its slots downward): each player's
 * handler - twice on every other tick with the shoe (L_1D03) - then the main loop's deaths, respawns
 * and joins. The C64 runs those on its main-loop pass; here each waits for a pass to have run (see
 * `mainLoop`).
 */
export function updatePlayers(world: World, commands: readonly [Command, Command]): void {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    const m = p.motion;
    m.joy = joyBits(commands[id]);
    m.walked = false;
    // ITEMS: under the power heart (state $18, handler $28FA) the handler runs twice every tick and the
    // colour flips (D_8548 EOR 5); the shoe (D_8728, cleared by D_34A0) waits until the round ends.
    const heart = heartSpeed(world, id);
    if (heart || (m.shoe && (world.clock.frame & 2) === 0)) runPlayer(world, p);
    runPlayer(world, p);
    if (heart) m.flash = !m.flash;
    p.facing = facingOf(m.frame, p.facing);
  }
  mainLoop(world);
}

/** Facing from the sprite frame: 0-7 by bit 2, the blow frames 8-$B by bit 1; dying frames keep it. */
function facingOf(frame: number, was: Facing): Facing {
  if (frame < 8) return frame & 4 ? -1 : 1;
  if (frame < 0x0c) return frame & 2 ? -1 : 1;
  return was;
}

/**
 * Start a new jump now (D_2162's bounce path into D_222B): the jump and fall cleared, Y onto the grid
 * parity, drift from the controls held this tick. Raises `jump`. For a stream that decides a bounce
 * itself; the players' own bounce on bubbles (with its 10 points and `bounce` event) is in their tick.
 */
export function bounce(world: World, id: PlayerId): void {
  const p = world.players[id];
  if (p.state !== 'play') return;
  restartJump(p, p.motion.frame);
  world.events.push({ type: 'jump', player: id });
}

/**
 * Can the level end with this player as it is (02 §9.1)? The C64 ($15FB) waits only for players who
 * are dying or dead-awaiting-respawn; standing, walking, jumping or falling all let the level end.
 */
export function isSettled(world: World, id: PlayerId): boolean {
  return world.players[id].state !== 'dying';
}

/**
 * Is the game over for good (02 §9.2)? Nobody in play or dying, and nobody can continue: no continues
 * left, or the C64 continue bug is on (then the game ends on the pass the last life goes, `$0A64`).
 * With continues left and the bug fixed the game waits for fire; any countdown is the caller's.
 */
export function gameEnded(world: World): boolean {
  if (world.players.some((p) => p.state === 'play' || p.state === 'dying')) return false;
  return world.continues <= 0 || world.options.c64Continue;
}
